import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import {
  MACRO_DIMENSIONS, MACRO_DIMENSION_KEYS, MACRO_PER_CELL, MACRO_QUADRANTS, MACRO_QUADRANT_KEYS,
  type Macro, type MacroDimension, type MacroFactor, type MacroQuadrant,
} from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Macro Cenário — a matriz, as contas e as leituras.

   Matriz: forças, fraquezas, oportunidades e ameaças nas linhas; as seis
   dimensões do PESTAL nas colunas. Cada célula guarda até quatro fatores,
   escritos pela organização (ou trazidos das sugestões da IA e editados).

   Síntese consolidada: inteira da IA — os principais fatores e o impacto
   estratégico de cada linha, os direcionamentos e a validação final. As
   dimensões predominantes são contadas aqui, da matriz.
   ───────────────────────────────────────────────────────────────────── */

export const MACRO_QUADRANT_META: Record<MacroQuadrant, { label: string; symbol: string; desc: string; internal: boolean }> = {
  forca:        { label: 'Forças',        symbol: 'F+', desc: 'O que a organização já tem a seu favor.',  internal: true },
  fraqueza:     { label: 'Fraquezas',     symbol: 'F−', desc: 'O que limita a organização hoje.',          internal: true },
  oportunidade: { label: 'Oportunidades', symbol: 'O+', desc: 'O que o contexto abre para a organização.', internal: false },
  ameaca:       { label: 'Ameaças',       symbol: 'A−', desc: 'O que o contexto pode tirar dela.',          internal: false },
};
export const MACRO_DIMENSION_META: Record<MacroDimension, { label: string; plural: string; desc: string }> = {
  politico:    { label: 'Político',    plural: 'Políticos',    desc: 'Governo, políticas públicas, financiamento e relações institucionais.' },
  economico:   { label: 'Econômico',   plural: 'Econômicos',   desc: 'Renda, crédito, preços, custos e o mercado.' },
  social:      { label: 'Social',      plural: 'Sociais',      desc: 'Comportamento, expectativas e demografia do público.' },
  ambiental:   { label: 'Ambiental',   plural: 'Ambientais',   desc: 'Clima, recursos naturais e sustentabilidade.' },
  tecnologico: { label: 'Tecnológico', plural: 'Tecnológicos', desc: 'Tecnologias, dados, digitalização e infraestrutura.' },
  legal:       { label: 'Legal',       plural: 'Legais',       desc: 'Leis, regulação, normas e compliance.' },
};

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
/** o mesmo fator, escrito de outro jeito (caixa, acentos, pontuação) */
export const sameMacroText = (a: string, b: string) => fold(a) === fold(b);

/**
 * Ids estáveis (mf-N) e no máximo quatro fatores por célula. Fator vazio
 * fica — é a linha que a pessoa acabou de abrir; a tela limpa o que ficou
 * vazio quando ela sai dele.
 */
export function withMacroIds(c: Macro): Macro {
  const used = new Set<string>();
  const perCell = new Map<string, number>();
  let n = 0;
  const factors: MacroFactor[] = [];
  for (const f of c.factors) {
    const cell = `${f.quadrant}:${f.dimension}`;
    if ((perCell.get(cell) ?? 0) >= MACRO_PER_CELL) continue;
    perCell.set(cell, (perCell.get(cell) ?? 0) + 1);
    let id = f.id && !used.has(f.id) ? f.id : '';
    while (!id || used.has(id)) id = `mf-${++n}`;
    used.add(id);
    factors.push({ ...f, id });
  }
  return { factors };
}

export const macroCell = (c: Macro, q: MacroQuadrant, d: MacroDimension) => c.factors.filter(f => f.quadrant === q && f.dimension === d);

/** As contas da matriz: por linha, por coluna, cobertura. */
export function macroMetrics(c: Macro) {
  const fs = c.factors.filter(f => f.text.trim());
  const count = (q?: MacroQuadrant, d?: MacroDimension) => fs.filter(f => (!q || f.quadrant === q) && (!d || f.dimension === d)).length;
  const byDimension = MACRO_DIMENSIONS.map(([key]) => ({ key, label: MACRO_DIMENSION_META[key].plural, count: count(undefined, key) }));
  const quadrants = MACRO_QUADRANTS.map(([key]) => {
    const dims = MACRO_DIMENSIONS.map(([d]) => ({ key: d, label: MACRO_DIMENSION_META[d].label, count: count(key, d) }))
      .filter(x => x.count > 0).sort((a, b) => b.count - a.count);
    return { key, label: MACRO_QUADRANT_META[key].label, count: count(key), dims };
  });
  const cells = MACRO_QUADRANTS.reduce((n, [q]) => n + MACRO_DIMENSIONS.filter(([d]) => count(q, d) > 0).length, 0);
  const covered = quadrants.filter(q => q.count > 0).length;
  return {
    total: fs.length, cells, byDimension, quadrants, covered,
    dimensions: byDimension.filter(d => d.count > 0).length,
    /** leitura interna (forças e fraquezas) e externa (oportunidades e ameaças) */
    internal: count('forca') > 0 && count('fraqueza') > 0,
    external: count('oportunidade') > 0 && count('ameaca') > 0,
  };
}
export type MacroMetrics = ReturnType<typeof macroMetrics>;

/** Há o bastante para a síntese: quatro fatores, em pelo menos dois quadrantes. */
export const macroReady = (c: Macro) => { const m = macroMetrics(c); return m.total >= 4 && m.covered >= 2; };

/* ── a síntese consolidada (a IA) ─────────────────────────────────── */

export const MACRO_CRITERIA = [['clareza', 'Clareza'], ['coerencia', 'Coerência'], ['amplitude', 'Amplitude'], ['utilidade', 'Utilidade']] as const;
export type MacroCriterion = (typeof MACRO_CRITERIA)[number][0];
const nota = z.number().int().min(0).max(100);
const linha = z.object({
  /** os principais fatores da linha, numa frase */
  fatores: z.string().trim().max(360).default(''),
  /** o que eles significam para a estratégia */
  impacto: z.string().trim().max(300).default(''),
});
export const macroReadingSchema = z.object({
  quadrantes: z.object({ forca: linha, fraqueza: linha, oportunidade: linha, ameaca: linha }),
  direcionamentos: z.array(z.string().trim().min(8).max(220)).min(1).max(5),
  validacao: z.object({
    score: nota,
    criterios: z.object({ clareza: nota, coerencia: nota, amplitude: nota, utilidade: nota }),
    titulo: z.string().trim().min(3).max(70),
    texto: z.string().trim().max(260).default(''),
  }),
  /** o quanto a leitura sustenta o propósito e o objetivo central, 0–100 */
  alinhamento: nota,
});
export type MacroReading = z.infer<typeof macroReadingSchema>;
export type MacroReadingDto = ToolReadingDto<MacroReading>;

/* ── as sugestões por célula (a IA) ───────────────────────────────── */

export const macroSuggestionSchema = z.object({
  items: z.array(z.object({
    quadrant:  z.enum(MACRO_QUADRANT_KEYS),
    dimension: z.enum(MACRO_DIMENSION_KEYS),
    text:      z.string().trim().min(8).max(220),
  })).max(72),
});
export type MacroSuggestions = z.infer<typeof macroSuggestionSchema>;
export interface MacroSuggestionSet extends MacroSuggestions { at: string }
export type MacroSuggestionDto = ToolReadingDto<MacroSuggestionSet>;

/** As sugestões de uma célula que ainda não estão na matriz. */
export function macroSuggestionsFor(s: MacroSuggestions | null, c: Macro, q: MacroQuadrant, d: MacroDimension, n = 3): string[] {
  if (!s) return [];
  const have = c.factors.map(f => f.text);
  return s.items.filter(x => x.quadrant === q && x.dimension === d && !have.some(h => sameMacroText(h, x.text))).map(x => x.text).slice(0, n);
}
